import React from 'react';
import { DollarSign, CheckCircle, XCircle, Copy, Info } from 'lucide-react';
import type { GoDaddyPricingToolResult, ToolResultRendererProps } from '../../../types/toolResults';
import Tooltip from '../../common/Tooltip';

/**
 * Renderer for GoDaddy domain pricing information.
 *
 * Displays:
 * - Domain name and availability status
 * - Registration pricing
 * - Currency information
 * - Visual price breakdown
 */
const DomainPricingRenderer: React.FC<ToolResultRendererProps<GoDaddyPricingToolResult>> = ({
  result,
  onCopy,
  className = ''
}) => {
  const { domain, registration_price, currency = 'USD', available } = result;

  const handleCopy = (text: string) => {
    if (onCopy) {
      onCopy(text);
    } else {
      navigator.clipboard.writeText(text);
    }
  };

  const formatPrice = (priceInCents?: number): string => {
    if (!priceInCents) return 'N/A';
    const price = priceInCents / 100;
    return price.toFixed(2);
  };

  const priceFormatted = formatPrice(registration_price);

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header Card */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/40 rounded-full flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white">Domain Pricing Information</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Pricing details for {domain}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Domain Status Card */}
      <div className="bg-white dark:bg-cap-dark border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Domain Information */}
          <div>
            <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Domain</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-lg font-semibold text-gray-900 dark:text-warm-white">{domain}</span>
                <Tooltip content="Copy domain name">
                  <button
                    onClick={() => handleCopy(domain)}
                    className="inline-flex items-center space-x-1 text-xs text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 transition-colors"
                  >
                    <Copy className="w-3 h-3" />
                    <span>Copy</span>
                  </button>
                </Tooltip>
              </div>

              <div className="flex items-center space-x-2">
                {available ? (
                  <>
                    <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                    <span className="text-sm font-medium text-green-800 dark:text-green-300">Available for Registration</span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                    <span className="text-sm font-medium text-red-800 dark:text-red-300">Not Available</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Pricing Information */}
          <div>
            <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Pricing</h4>
            <div className="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
              <div className="flex items-baseline justify-between">
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Registration Price</p>
                  <div className="flex items-baseline space-x-1">
                    <span className="text-3xl font-bold text-gray-900 dark:text-warm-white">${priceFormatted}</span>
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{currency}</span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">per year</p>
                </div>
                <DollarSign className="w-8 h-8 text-purple-400" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Breakdown */}
      {registration_price && (
        <div className="bg-white dark:bg-cap-dark border border-gray-200 dark:border-gray-700 rounded-lg p-6">
          <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4">
            Pricing Breakdown
          </h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
              <span className="text-sm text-gray-700 dark:text-gray-300">1 Year Registration</span>
              <span className="text-sm font-semibold text-gray-900 dark:text-warm-white">${priceFormatted} {currency}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
              <span className="text-sm text-gray-700 dark:text-gray-300">2 Year Registration</span>
              <span className="text-sm font-semibold text-gray-900 dark:text-warm-white">
                ${(parseFloat(priceFormatted) * 2).toFixed(2)} {currency}
              </span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
              <span className="text-sm text-gray-700 dark:text-gray-300">5 Year Registration</span>
              <span className="text-sm font-semibold text-gray-900 dark:text-warm-white">
                ${(parseFloat(priceFormatted) * 5).toFixed(2)} {currency}
              </span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-gray-700 dark:text-gray-300">10 Year Registration</span>
              <span className="text-sm font-semibold text-gray-900 dark:text-warm-white">
                ${(parseFloat(priceFormatted) * 10).toFixed(2)} {currency}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Information Note */}
      <div className="bg-primary-50 dark:bg-blue-900/20 border border-primary-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Info className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-1">Pricing Information</h4>
            <p className="text-sm text-blue-800 dark:text-blue-400">
              Prices shown are for registration only. Renewal prices may differ.
              Additional services like privacy protection or DNS management may incur extra costs.
              Multi-year registrations are calculated at the current annual rate.
            </p>
          </div>
        </div>
      </div>

      {/* Action Button */}
      {available && (
        <div className="flex justify-end">
          <button
            onClick={() => {
              const text = `${domain} - $${priceFormatted} ${currency}/year`;
              handleCopy(text);
            }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-900/40 rounded-md transition-colors border border-purple-300 dark:border-purple-800"
          >
            <Copy className="w-4 h-4" />
            <span>Copy Pricing Summary</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default DomainPricingRenderer;
